import { GraduationCap, Globe } from "lucide-react";

/**
 * ScopeChip — inline pill that tells families whether a graduate-
 * outcome number is measured for THIS SPECIFIC COURSE at this
 * university, or is a broader signal drawn from all graduates on
 * degrees of this type nationally.
 *
 * Why:  HESA / KIS Discover Uni publishes per-course cohorts for a
 * handful of key numbers (employment %, 15-month salary, LEO 3/5-yr
 * salary — all keyed on the KIS course code). Everything else on the
 * Outcomes section — DAAD German subject baselines, US Scorecard
 * subject-level earnings, national LEO sector charts — is
 * subject-level context, not course-level truth. Users have
 * repeatedly asked "is this THIS course or graduates of similar
 * courses everywhere?". This chip is the honest one-glance answer.
 *
 * Variants:
 *   * course  — emerald "This course" (per-course HESA / KIS cohort)
 *   * subject — amber   "Subject-wide" (national subject baseline)
 *   * regional— sky     "By UK region"  (LEO regional / KIS regional cut)
 *
 * All variants render inline, so they sit unobtrusively at the end of
 * a DataRow value or next to a section subheading.
 */
export function ScopeChip({ scope = "course", note, testId }) {
  const cfg = {
    course: {
      cls: "border-emerald-300 bg-emerald-50 text-emerald-800",
      Icon: GraduationCap,
      label: "This course",
      defaultTitle: "Measured for this specific course cohort at this university.",
    },
    subject: {
      cls: "border-amber-300 bg-amber-50 text-amber-900",
      Icon: Globe,
      label: "Subject-wide",
      defaultTitle: "National baseline across all degrees of this type — not course-specific.",
    },
    regional: {
      cls: "border-sky-300 bg-sky-50 text-sky-800",
      Icon: Globe,
      label: "By region",
      defaultTitle: "Regional average across all graduates in this subject nationally.",
    },
  }[scope] || null;
  if (!cfg) return null;
  const { cls, Icon, label, defaultTitle } = cfg;
  return (
    <span
      data-testid={testId || `scope-chip-${scope}`}
      title={note || defaultTitle}
      className={
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-underground-sc tracking-wider ml-1.5 " +
        cls
      }
    >
      <Icon className="h-2.5 w-2.5" aria-hidden />
      {label}
    </span>
  );
}

/**
 * OutcomeScopeLegend — one-line legend printed at the top of the
 * Outcomes section explaining the two chip meanings, so students
 * don't have to hover to understand the scope difference.
 */
export function OutcomeScopeLegend({ testId = "outcome-scope-legend" }) {
  return (
    <div
      data-testid={testId}
      className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-600"
    >
      <span className="font-underground-sc tracking-wider text-stone-500 mr-1">
        What's specific to this course vs subject-wide?
      </span>
      <span className="inline-flex items-center gap-1">
        <ScopeChip scope="course" testId={`${testId}-course-example`} />
        <span>this exact course cohort</span>
      </span>
      <span className="inline-flex items-center gap-1">
        <ScopeChip scope="subject" testId={`${testId}-subject-example`} />
        <span>all UK/US grads in this subject nationally</span>
      </span>
    </div>
  );
}

export default ScopeChip;
